// WS-8 P1 — Facturación · Programación por OP (vista 1 de 4).
//
// El coordinador carga acá la programación diaria por Orden de Proceso que
// Alpina manda (Excel del "Seguimiento semanal" o alta manual), con OP
// atrasadas y recargas (upsert). En P2 esta programación se cruza automática
// contra las capturas VALIDADAS de los líderes.
//
// Estructura:
//   - Filtros: rango de fechas · cliente · área
//   - Acciones: Importar Excel · Agregar manual
//   - Tabla agrupada por fecha (OP · SKU · cantidad · origen · observación)
//   - Por fila: Corregir (con motivo ≥20, auditado) · Eliminar (soft, razón)

import { useMemo, useState } from 'react';
import { Fragment } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'sonner';
import { CoordinadorSidebar } from '../../components/layout/CoordinadorSidebar';
import { RoleLayout } from '../../components/layout/RoleLayout';
import type { ProgramacionOp, ProgramacionOpList } from 'schemas';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select } from '../../components/ui/select';
import { Spinner } from '../../components/ui/spinner';
import { Textarea } from '../../components/ui/textarea';
import { ApiError } from '../../lib/api/client';
import {
  useCrearProgramacionOp,
  useCorregirProgramacionOp,
  useEliminarProgramacionOp,
  useProgramacionOp,
} from '../../lib/api/queries';
import { useAuth } from '../../lib/auth';
import { FactImportarProgramacionOpDialog } from './FactImportarProgramacionOpDialog';
import { PeriodoSelector, usePeriodoRango } from './factPeriodo';

type ModalState =
  | { kind: 'cerrado' }
  | { kind: 'importar' }
  | { kind: 'alta' }
  | { kind: 'corregir'; fila: ProgramacionOp }
  | { kind: 'eliminar'; fila: ProgramacionOp };

const DOW = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];
function fmtFecha(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return iso;
  return `${DOW[new Date(Date.UTC(y, m - 1, d)).getUTCDay()]} ${d}/${String(m).padStart(2, '0')}`;
}

export function FacturacionProgramacionOpPage() {
  const { state, signOut } = useAuth();
  const { periodo, setPeriodo, rango, setRango, rangoValido, rangoEfectivo } =
    usePeriodoRango('semana');
  const [clienteId, setClienteId] = useState('');
  const [areaId, setAreaId] = useState('');
  const [modal, setModal] = useState<ModalState>({ kind: 'cerrado' });

  const { data, isLoading, isError, error, isFetching } = useProgramacionOp({
    desde: rangoEfectivo.desde,
    hasta: rangoEfectivo.hasta,
    cliente_id: clienteId || undefined,
    area_id: areaId || undefined,
  });

  if (state.kind !== 'signed_in') return <Navigate to="/login" replace />;

  if (isLoading) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-3 p-6">
        <Spinner className="size-6" />
        <p className="text-sm text-fg-tertiary">Cargando programación por OP…</p>
      </main>
    );
  }

  if (isError || !data) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <h1 className="text-lg font-medium text-crit">No pudimos cargar</h1>
        <p className="text-sm text-fg-secondary">
          {error instanceof Error ? error.message : 'Error desconocido'}
        </p>
        <Button onClick={() => signOut()} variant="outline">
          Cerrar sesión
        </Button>
      </main>
    );
  }

  return (
    <RoleLayout sidebar={<CoordinadorSidebar />} roleLabel="Coordinador">
      <div className="flex flex-col gap-4">
        <header className="flex flex-wrap items-end justify-between gap-3 border-b border-border pb-3">
          <div>
            <h1 className="text-xl font-medium text-fg">Programación por OP</h1>
            <p className="text-sm text-fg-tertiary">
              Órdenes de proceso que manda el cliente, día a día. Se cruzan automático contra la
              producción validada.
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setModal({ kind: 'alta' })}>
              Agregar manual
            </Button>
            <Button onClick={() => setModal({ kind: 'importar' })}>Importar Excel</Button>
          </div>
        </header>

        {/* Filtros */}
        <div className="flex flex-wrap items-end gap-3">
          <PeriodoSelector
            periodo={periodo}
            onPeriodo={setPeriodo}
            rango={rango}
            onRango={setRango}
            rangoValido={rangoValido}
          />
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="f-cliente">Cliente</Label>
            <Select
              id="f-cliente"
              value={clienteId}
              onChange={(e) => setClienteId(e.target.value)}
              className="w-44"
            >
              <option value="">Todos</option>
              {data.clientes.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="f-area">Área</Label>
            <Select
              id="f-area"
              value={areaId}
              onChange={(e) => setAreaId(e.target.value)}
              className="w-52"
            >
              <option value="">Todas</option>
              {data.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.codigo} · {a.nombre}
                </option>
              ))}
            </Select>
          </div>
          {isFetching ? <Spinner className="mb-2 size-4" /> : null}
        </div>

        <TablaProgramacion
          filas={data.filas}
          onCorregir={(fila) => setModal({ kind: 'corregir', fila })}
          onEliminar={(fila) => setModal({ kind: 'eliminar', fila })}
        />
      </div>

      {modal.kind === 'importar' ? (
        <FactImportarProgramacionOpDialog
          data={data}
          onClose={() => setModal({ kind: 'cerrado' })}
        />
      ) : null}
      {modal.kind === 'alta' ? (
        <AltaManualDialog data={data} onClose={() => setModal({ kind: 'cerrado' })} />
      ) : null}
      {modal.kind === 'corregir' ? (
        <CorregirDialog
          data={data}
          fila={modal.fila}
          onClose={() => setModal({ kind: 'cerrado' })}
        />
      ) : null}
      {modal.kind === 'eliminar' ? (
        <EliminarDialog fila={modal.fila} onClose={() => setModal({ kind: 'cerrado' })} />
      ) : null}
    </RoleLayout>
  );
}

// ============================================================================
// Tabla agrupada por fecha
// ============================================================================

function TablaProgramacion({
  filas,
  onCorregir,
  onEliminar,
}: {
  filas: ProgramacionOp[];
  onCorregir: (fila: ProgramacionOp) => void;
  onEliminar: (fila: ProgramacionOp) => void;
}) {
  const grupos = useMemo(() => {
    const byFecha = new Map<string, ProgramacionOp[]>();
    for (const f of filas) {
      const arr = byFecha.get(f.fecha_programada) ?? [];
      arr.push(f);
      byFecha.set(f.fecha_programada, arr);
    }
    return [...byFecha.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [filas]);

  if (filas.length === 0) {
    return (
      <div className="rounded border border-dashed border-border p-8 text-center text-sm text-fg-tertiary">
        No hay programación en el rango. Importe el Excel del cliente o agregue una OP manual.
      </div>
    );
  }

  return (
    <div className="overflow-auto rounded border border-border">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-surface-overlay text-xs text-fg-tertiary">
          <tr>
            <th className="px-3 py-2 text-left">OP</th>
            <th className="px-3 py-2 text-left">SKU</th>
            <th className="px-3 py-2 text-left">Área</th>
            <th className="px-3 py-2 text-right">Cant. programada</th>
            <th className="px-3 py-2 text-left">Origen</th>
            <th className="px-3 py-2 text-left">Observación</th>
            <th className="px-3 py-2 text-right">Acciones</th>
          </tr>
        </thead>
        <tbody>
          {grupos.map(([fecha, filasDia]) => {
            const totalDia = filasDia.reduce((a, f) => a + f.cantidad_programada, 0);
            return (
              <Fragment key={fecha}>
                <tr className="border-t-2 border-border-strong bg-surface-overlay/60">
                  <td colSpan={3} className="px-3 py-1.5 font-medium text-fg-secondary">
                    {fmtFecha(fecha)} <span className="text-fg-tertiary">· {fecha}</span>
                  </td>
                  <td className="px-3 py-1.5 text-right tabular-nums text-fg-secondary">
                    {totalDia.toLocaleString('es-CO')}
                  </td>
                  <td colSpan={3} className="px-3 py-1.5 text-xs text-fg-tertiary">
                    {filasDia.length} OP(s)
                  </td>
                </tr>
                {filasDia.map((f) => (
                  <tr key={f.id} className="border-t border-border">
                    <td className="px-3 py-2 tabular-nums">
                      {f.numero_op ?? (
                        <span className="text-fg-tertiary" title="Programación sin OP asignada">
                          sin OP
                        </span>
                      )}
                      {f.corregido_at ? (
                        <Badge tone="warn" className="ml-2" title={f.razon_correccion ?? undefined}>
                          corregida
                        </Badge>
                      ) : null}
                    </td>
                    <td className="px-3 py-2">
                      <div className="font-medium">{f.sku_codigo_cliente}</div>
                      <div className="text-xs text-fg-tertiary">{f.sku_descripcion}</div>
                    </td>
                    <td className="px-3 py-2 text-xs">
                      {f.area_codigo} · {f.area_nombre}
                      <div className="text-fg-tertiary">{f.planta_nombre}</div>
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {f.cantidad_programada.toLocaleString('es-CO')}
                    </td>
                    <td className="px-3 py-2 text-xs text-fg-tertiary">
                      {f.origen === 'excel_alpina' ? 'Excel' : 'Manual'}
                    </td>
                    <td className="max-w-48 truncate px-3 py-2 text-xs text-fg-tertiary">
                      {f.observacion ?? '—'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="sm" variant="ghost" onClick={() => onCorregir(f)}>
                          Corregir
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => onEliminar(f)}>
                          Eliminar
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// ============================================================================
// Alta manual
// ============================================================================

function AltaManualDialog({ data, onClose }: { data: ProgramacionOpList; onClose: () => void }) {
  const clientesOrdenados = [...data.clientes].sort(
    (a, b) => Number(b.es_principal) - Number(a.es_principal),
  );
  const [clienteId, setClienteId] = useState(clientesOrdenados[0]?.id ?? '');
  const [areaId, setAreaId] = useState('');
  const [fecha, setFecha] = useState(new Date().toISOString().slice(0, 10));
  const [numeroOp, setNumeroOp] = useState('');
  const [skuId, setSkuId] = useState('');
  const [cantidad, setCantidad] = useState('');
  const [observacion, setObservacion] = useState('');
  const crear = useCrearProgramacionOp();

  const skusDelCliente = data.skus.filter((s) => s.cliente_id === clienteId);
  const cant = Number(cantidad);
  // 0 es válido (OP reprogramada/cancelada); vacío no — Number('') da 0.
  const cantValida = cantidad.trim() !== '' && Number.isFinite(cant) && cant >= 0;
  const opValida = numeroOp === '' || /^\d{8}$/.test(numeroOp);
  const puedeGuardar =
    clienteId !== '' && areaId !== '' && skuId !== '' && fecha !== '' && cantValida && opValida;

  async function guardar() {
    if (!puedeGuardar) return;
    try {
      await crear.mutateAsync({
        cliente_id: clienteId,
        area_id: areaId,
        fecha_programada: fecha,
        numero_op: numeroOp === '' ? null : numeroOp,
        sku_id: skuId,
        cantidad_programada: cant,
        observacion: observacion.trim() === '' ? null : observacion.trim(),
      });
      toast.success('Programación agregada');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al guardar';
      toast.error('No se pudo guardar', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Agregar programación por OP</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="alta-cliente">Cliente</Label>
            <Select
              id="alta-cliente"
              value={clienteId}
              onChange={(e) => {
                setClienteId(e.target.value);
                setSkuId('');
              }}
            >
              {clientesOrdenados.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="alta-fecha">Fecha programada</Label>
            <Input
              id="alta-fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="alta-op">OP (8 dígitos, vacío si no hay)</Label>
            <Input
              id="alta-op"
              inputMode="numeric"
              maxLength={8}
              placeholder="10349726"
              value={numeroOp}
              onChange={(e) => setNumeroOp(e.target.value.replace(/\D/g, ''))}
            />
            {!opValida ? <p className="text-xs text-crit">Debe tener 8 dígitos</p> : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="alta-cant">Cantidad programada</Label>
            <Input
              id="alta-cant"
              inputMode="numeric"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
            />
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="alta-sku">SKU</Label>
            <Select id="alta-sku" value={skuId} onChange={(e) => setSkuId(e.target.value)}>
              <option value="">Elija un SKU…</option>
              {skusDelCliente.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.codigo_cliente} — {s.descripcion}
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="alta-area">Área</Label>
            <Select id="alta-area" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
              <option value="">Elija un área…</option>
              {data.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.codigo} · {a.nombre} ({a.planta_nombre})
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="alta-obs">Observación (opcional)</Label>
            <Textarea
              id="alta-obs"
              rows={2}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={!puedeGuardar || crear.isPending}>
            {crear.isPending ? 'Guardando…' : 'Guardar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Corregir (con motivo obligatorio ≥20 — queda auditado)
// ============================================================================

function CorregirDialog({
  data,
  fila,
  onClose,
}: {
  data: ProgramacionOpList;
  fila: ProgramacionOp;
  onClose: () => void;
}) {
  const [fecha, setFecha] = useState(fila.fecha_programada);
  const [numeroOp, setNumeroOp] = useState(fila.numero_op ?? '');
  const [skuId, setSkuId] = useState(fila.sku_id);
  const [areaId, setAreaId] = useState(fila.area_id);
  const [cantidad, setCantidad] = useState(String(fila.cantidad_programada));
  const [observacion, setObservacion] = useState(fila.observacion ?? '');
  const [razon, setRazon] = useState('');
  const corregir = useCorregirProgramacionOp();

  const skusDelCliente = data.skus.filter((s) => s.cliente_id === fila.cliente_id);
  const cant = Number(cantidad);
  const cantValida = cantidad.trim() !== '' && Number.isFinite(cant) && cant >= 0;
  const opValida = numeroOp === '' || /^\d{8}$/.test(numeroOp);
  const puedeGuardar = razon.trim().length >= 20 && cantValida && opValida && fecha !== '';

  async function guardar() {
    if (!puedeGuardar) return;
    try {
      await corregir.mutateAsync({
        id: fila.id,
        fecha_programada: fecha !== fila.fecha_programada ? fecha : undefined,
        numero_op:
          (numeroOp === '' ? null : numeroOp) !== fila.numero_op
            ? numeroOp === ''
              ? null
              : numeroOp
            : undefined,
        sku_id: skuId !== fila.sku_id ? skuId : undefined,
        area_id: areaId !== fila.area_id ? areaId : undefined,
        cantidad_programada: cant !== fila.cantidad_programada ? cant : undefined,
        observacion:
          (observacion.trim() === '' ? null : observacion.trim()) !== fila.observacion
            ? observacion.trim() === ''
              ? null
              : observacion.trim()
            : undefined,
        razon: razon.trim(),
      });
      toast.success('Programación corregida');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al corregir';
      toast.error('No se pudo corregir', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-lg">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Corregir programación · {fila.numero_op ?? 'sin OP'} · {fila.sku_codigo_cliente}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cor-fecha">Fecha programada</Label>
            <Input
              id="cor-fecha"
              type="date"
              value={fecha}
              onChange={(e) => setFecha(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cor-op">OP (vacío = sin OP)</Label>
            <Input
              id="cor-op"
              inputMode="numeric"
              maxLength={8}
              value={numeroOp}
              onChange={(e) => setNumeroOp(e.target.value.replace(/\D/g, ''))}
            />
            {!opValida ? <p className="text-xs text-crit">Debe tener 8 dígitos</p> : null}
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cor-cant">Cantidad programada</Label>
            <Input
              id="cor-cant"
              inputMode="numeric"
              value={cantidad}
              onChange={(e) => setCantidad(e.target.value)}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="cor-area">Área</Label>
            <Select id="cor-area" value={areaId} onChange={(e) => setAreaId(e.target.value)}>
              {data.areas.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.codigo} · {a.nombre}
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="cor-sku">SKU</Label>
            <Select id="cor-sku" value={skuId} onChange={(e) => setSkuId(e.target.value)}>
              {skusDelCliente.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.codigo_cliente} — {s.descripcion}
                </option>
              ))}
            </Select>
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="cor-obs">Observación</Label>
            <Textarea
              id="cor-obs"
              rows={2}
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
            />
          </div>
          <div className="col-span-2 flex flex-col gap-1.5">
            <Label htmlFor="cor-razon">Motivo de la corrección (mín. 20 caracteres)</Label>
            <Textarea
              id="cor-razon"
              rows={2}
              placeholder="Ej: Alpina reprogramó la orden — mail del 01/07"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
            />
            {razon.trim().length > 0 && razon.trim().length < 20 ? (
              <p className="text-xs text-crit">Faltan {20 - razon.trim().length} caracteres</p>
            ) : null}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button onClick={guardar} disabled={!puedeGuardar || corregir.isPending}>
            {corregir.isPending ? 'Guardando…' : 'Corregir'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ============================================================================
// Eliminar (soft — razón obligatoria)
// ============================================================================

function EliminarDialog({ fila, onClose }: { fila: ProgramacionOp; onClose: () => void }) {
  const [razon, setRazon] = useState('');
  const eliminar = useEliminarProgramacionOp();
  const puedeEliminar = razon.trim().length >= 5;

  async function confirmar() {
    if (!puedeEliminar) return;
    try {
      await eliminar.mutateAsync({ id: fila.id, razon: razon.trim() });
      toast.success('Programación eliminada');
      onClose();
    } catch (err) {
      const msg = err instanceof ApiError ? err.message : 'Error al eliminar';
      toast.error('No se pudo eliminar', { description: msg });
    }
  }

  return (
    <Dialog open onClose={onClose} className="max-w-md">
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Eliminar programación</DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-3">
          <p className="text-sm text-fg-secondary">
            {fila.fecha_programada} · OP {fila.numero_op ?? 'sin OP'} · {fila.sku_codigo_cliente} ·{' '}
            {fila.cantidad_programada.toLocaleString('es-CO')} und.
          </p>
          <p className="text-xs text-fg-tertiary">
            La fila deja de contar para el cruce y el nivel de servicio, pero queda en el historial
            (auditoría). Después puede volver a cargar la misma OP.
          </p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="del-razon">Razón (mín. 5 caracteres)</Label>
            <Textarea
              id="del-razon"
              rows={2}
              placeholder="Ej: carga duplicada"
              value={razon}
              onChange={(e) => setRazon(e.target.value)}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            variant="danger"
            onClick={confirmar}
            disabled={!puedeEliminar || eliminar.isPending}
          >
            {eliminar.isPending ? 'Eliminando…' : 'Eliminar'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
